import React, { useMemo, useState, useContext } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { DataGrid, KeenIcon } from '@/components'
import { Loading } from '@/layouts/buddy/loading'
import { enqueueSnackbar } from 'notistack'
import { AuthContext } from '@/auth/providers/JWTProvider'
import { formatDate } from '@/pages/buddy/utils/Date'
import { DomainActivityData, DomainUser } from '@/pages/buddy/types/DomainActivity'

interface Props {
    domains: DomainActivityData[]
    loading: boolean
}

export const DomainActivity: React.FC<Props> = ({ domains, loading }) => {
    const auth = useContext(AuthContext)
    const [selected, setSelected] = useState<DomainActivityData | null>(null)

    const handleImpersonate = async (p_id: string) => {
        if (!auth?.impersonate) {
            enqueueSnackbar('Impersonation not available', { variant: 'solid', state: 'danger' })
            return
        }
        try {
            await auth.impersonate(p_id)
        } catch {
            enqueueSnackbar('Failed to impersonate', { variant: 'solid', state: 'danger' })
        }
    }

    const domainColumns = useMemo<ColumnDef<DomainActivityData>[]>(() => [
        {
            accessorKey: 'domain_object_id',
            id: 'domain',
            header: () => 'Domain id',
            enableSorting: true,
            cell: info => <span className="text-gray-900 font-medium text-sm">{info.getValue<string>()}</span>
        },
        {
            accessorKey: 'company_count',
            header: () => 'Companies',
            enableSorting: true,
            cell: info => <span className="text-gray-700 font-normal text-sm">{info.getValue<number>()}</span>
        },
        {
            accessorKey: 'job_count',
            header: () => 'Jobs',
            enableSorting: true,
            cell: info => <span className="text-gray-700 font-normal text-sm">{info.getValue<number>()}</span>
        },
        {
            accessorKey: 'candidate_count',
            header: () => 'Candidates',
            enableSorting: true,
            cell: info => <span className="text-gray-700 font-normal text-sm">{info.getValue<number>()}</span>
        },
        {
            accessorKey: 'match_count',
            header: () => 'Matches',
            enableSorting: true,
            cell: info => <span className="text-gray-700 font-normal text-sm">{info.getValue<number>()}</span>
        },
        {
            accessorKey: 'last_activity',
            id: 'lastActivity',
            header: () => 'Last activity',
            enableSorting: true,
            cell: ({ getValue }) => {
                const iso = getValue<string>()
                return <span className="text-gray-700 font-normal text-sm">{formatDate(iso)}</span>
            },
            meta: {
                className: 'w-[200px]',
                cellClassName: 'text-gray-700 font-normal text-sm'
            }
        },
        {
            id: 'userCount',
            header: () => 'Users',
            enableSorting: true,
            cell: ({ row }) => <span className="text-gray-700 font-normal text-sm">{row.original.users.length}</span>
        },
        {
            id: 'viewUsers',
            header: () => '',
            enableSorting: false,
            cell: ({ row }) => (
                <button
                    className="btn btn-sm btn-icon btn-clear btn-light"
                    onClick={() => setSelected(row.original)}
                >
                    <KeenIcon icon="user" />
                </button>
            ),
            meta: { className: 'w-[60px]' }
        }
    ], [])

    const userColumns = useMemo<ColumnDef<DomainUser>[]>(() => [
        {
            id: 'name',
            header: () => 'Name',
            enableSorting: true,
            cell: ({ row }) => (
                <span className="text-gray-900 font-medium text-sm">
          {row.original.first_name} {row.original.last_name}
        </span>
            )
        },
        {
            accessorKey: 'email',
            header: () => 'Email',
            enableSorting: true,
            cell: info => <span className="text-gray-700 font-normal text-sm">{info.getValue<string>()}</span>
        },
        {
            accessorKey: 'company',
            header: () => 'Company',
            enableSorting: true,
            cell: info => <span className="text-gray-700 font-normal text-sm">{info.getValue<string>()}</span>
        },
        {
            id: 'roles',
            header: () => 'Roles',
            enableSorting: true,
            cell: ({ row }) => (
                <span className="text-gray-700 font-normal text-sm">{(row.original.roles || []).join(', ')}</span>
            )
        },
        {
            id: 'impersonate',
            header: () => '',
            enableSorting: false,
            cell: ({ row }) => (
                <button
                    className="btn btn-sm btn-icon btn-clear btn-light"
                    onClick={() => handleImpersonate(row.original.p_id)}
                >
                    <KeenIcon icon="lock" />
                </button>
            ),
            meta: { className: 'w-[60px]' }
        }
    ], [handleImpersonate])

    if (loading) return <Loading loading={loading} />

    return (
        <div className="space-y-6">
            <div className="card card-grid min-w-full">
                <div className="card-header flex-wrap py-5">
                    <h3 className="card-title">Domains overview</h3>
                </div>
                <div className="card-body">
                    <DataGrid
                        columns={domainColumns}
                        data={domains}
                        pagination={{ size: 10 }}
                        sorting={[{ id: 'domain', desc: false }]}
                    />
                </div>
            </div>

            {selected && (
                <div key={selected.domain_object_id} className="card card-grid min-w-full">
                    <div className="card-header flex-wrap py-5 flex justify-between items-center">
                        <h3 className="card-title">Users in domain {selected.domain_object_id}</h3>
                        <button
                            className="btn btn-sm btn-light"
                            onClick={() => setSelected(null)}
                        >
                            Close
                        </button>
                    </div>
                    <div className="card-body">
                        <DataGrid
                            key={selected.domain_object_id}
                            columns={userColumns}
                            data={selected.users}
                            pagination={{ size: 10 }}
                            sorting={[{ id: 'name', desc: false }]}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}
